import React, { useState } from 'react';
import axios from 'axios';

import CliveActionBanner from '~/components/Clive/CliveBanner/CliveActionBanner';
import type { DocumentMetadata } from '~/components/communications/types';
import type { UploadSubmitOverride } from '~/components/Documents/UploadDocumentsCard/types';
import { useCliveClaimContext } from '~/components/SystemConfiguration/CliveConfiguration/CliveClaimContext';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import Switch from '~/components/ui/atoms/Switch';
import { reportGenericException } from '~/Utils';
import { getSubOrganizationId } from '~/Utils/ClaimUtils';

import { CLIVE_FILES_ONLY_VALIDATION } from './constants';
import UploadDocument from './UploadDocumentsCard';

interface UploadClaimDocumentsWithCliveProps {
  claim?: ClaimPolymorph;
  onCancel: () => void;
  onSubmitDocument: (document: DocumentMetadata) => void;
  onCliveUploadSuccess?: () => void;
  initialValues?: {
    exposure_ids?: number[];
    type?: string;
  };
  forceCliveEnabled?: boolean;
}

const noop = () => {};

const UploadClaimDocumentsWithClive: React.FC<UploadClaimDocumentsWithCliveProps> = ({
  claim,
  onCancel,
  onSubmitDocument,
  onCliveUploadSuccess = noop,
  initialValues,
  forceCliveEnabled = false,
}) => {
  const { isCliveEnabled } = useCliveClaimContext();
  const subOrganizationId = getSubOrganizationId(claim);
  const isCliveDocumentsUploadEnabled = isCliveEnabled(subOrganizationId);

  // Early validation: if Clive is enabled but no claim ID, report error early
  if (isCliveDocumentsUploadEnabled && !claim?.id) {
    const error = new Error('Clive document upload requires a valid claim ID');
    reportGenericException(error);
  }

  // Use config result to populate the switch default state
  const [isCliveAnalysisEnabled, setIsCliveAnalysisEnabled] = useState<boolean>(
    forceCliveEnabled || isCliveDocumentsUploadEnabled
  );

  // Calculate Clive-specific parameters based on feature flag
  const isCliveModeActive = isCliveDocumentsUploadEnabled && isCliveAnalysisEnabled;
  const validation = isCliveModeActive ? CLIVE_FILES_ONLY_VALIDATION : undefined;

  // Clive submit function - completely overrides the default submit behavior
  // Error handling and setSubmitting are now handled by the base component
  const handleCliveSubmit: UploadSubmitOverride = async (values) => {
    // No need to check claim?.id here since we validate early in the component
    const storedFileIds = values?.files?.map((file) => file.stored_file_id).filter(Boolean);

    await axios.post(`/api/v1/claims/${claim!.id}/ai/auto_doc_upload`, {
      stored_file_ids: storedFileIds,
    });

    // For Clive uploads, we don't call onSubmitDocument since it's async processing
    // The document will be processed by AI and attached to the claim later
    // Call the success callback to refresh the documents list
    onCliveUploadSuccess();
  };

  // Only use Clive submit when enabled and analysis is on and we have a claim ID
  const submitOverride = isCliveModeActive && claim?.id ? handleCliveSubmit : undefined;

  // When forceCliveEnabled is true, don't show the toggle switch
  const bannerContentText =
    "Once the document upload is completed, I'll analyze the document to extract the relevant fields and generate a short summary." +
    (forceCliveEnabled ? '' : ' You can choose to turn off my analysis and enter the details manually instead.');

  const banner = isCliveDocumentsUploadEnabled ? (
    <CliveActionBanner
      title="Clive's Analysis"
      contentText={bannerContentText}
      actionComponent={
        forceCliveEnabled ? null : (
          <Switch
            checked={isCliveAnalysisEnabled}
            onChange={(event) => setIsCliveAnalysisEnabled(event.target.checked)}
            disabled={false}
            aria-label="Clive's Analysis"
          />
        )
      }
    />
  ) : undefined;

  return (
    <UploadDocument
      claim={claim}
      onCancel={onCancel}
      onSubmitDocument={onSubmitDocument}
      initialValues={initialValues}
      // Generic parameters for Clive functionality (undefined when Clive is disabled)
      customValidation={validation}
      onSubmitOverride={submitOverride}
      showFileBoxOnly={isCliveModeActive}
      banner={banner}
    />
  );
};

export default UploadClaimDocumentsWithClive;
